import { Card, Col, Row, Tabs } from 'antd'
import { Sparkles } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { Organization } from 'src/api/main'
import { SoonTag } from 'src/components'

import { ATTENTION_ITEMS } from './activityData'
import { ActivityFeedTab } from './ActivityFeedTab'
import { ActivityTicker } from './ActivityTicker'
import { AiInsightsTab } from './AiInsightsTab'
import { AttentionLane } from './AttentionLane'
import { CompanyGraphCanvas } from './CompanyGraphCanvas'
import { CATEGORY_COLORS, CATEGORY_ORDER } from './constants'
import { EdgeDetailPanel } from './EdgeDetailPanel'
import { KpiStrip } from './KpiStrip'
import { NodeDetailPanel } from './NodeDetailPanel'
import { useStyles } from './styles'
import type { GraphNode, InsightPeriod } from './types'
import { useBusinessActivity } from './useBusinessActivity'
import { useGraphData } from './useGraphData'
import { useLiveEvents } from './useLiveEvents'

type Props = {
  workspaceId: number
  organization: Organization | undefined
}

/** Left card tabs: the force-directed graph, or the company activity feed. */
type GraphTab = 'graph' | 'activity'

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(prefersReducedMotion)
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches)
    mq.addEventListener('change', onChange)

    return () => mq.removeEventListener('change', onChange)
  }, [])

  return reduced
}

const usePageHidden = () => {
  const [hidden, setHidden] = useState(
    typeof document !== 'undefined' ? document.hidden : false
  )
  useEffect(() => {
    const onChange = () => setHidden(document.hidden)
    document.addEventListener('visibilitychange', onChange)

    return () => document.removeEventListener('visibilitychange', onChange)
  }, [])

  return hidden
}

export const CompanyGraphView = ({ workspaceId, organization }: Props) => {
  const { styles, cx } = useStyles()
  const { t } = useTranslation()

  const { nodes, edges } = useGraphData({ workspaceId, organization })
  const reducedMotion = usePrefersReducedMotion()
  const pageHidden = usePageHidden()

  const agentsRunning = useMemo(
    () => nodes.filter((n) => n.kind === 'agent').length,
    [nodes]
  )

  const { feed, nodeActivity, edgeActivity, alerts, kpis, edgePing } =
    useLiveEvents(nodes, edges, pageHidden, agentsRunning)

  const businessEvents = useBusinessActivity(pageHidden)

  const [tab, setTab] = useState<GraphTab>('graph')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null)
  const [hoverId, setHoverId] = useState<string | null>(null)
  const [period, setPeriod] = useState<InsightPeriod>('today')

  // Selecting a node and selecting an edge are mutually exclusive — but only
  // clear the OTHER selection when actually selecting (truthy id), otherwise
  // clearing one (e.g. onSelectEdge(null) after a node click) would wipe the other.
  const selectNode = (id: string | null) => {
    setSelectedId(id)
    if (id) setSelectedEdgeId(null)
  }
  const selectEdge = (id: string | null) => {
    setSelectedEdgeId(id)
    if (id) setSelectedId(null)
  }

  // Bridge: a fresh business event ripples its related graph node.
  const flash = useMemo(() => {
    const ev = businessEvents[0]
    if (!ev) return null
    const firstOf = (kind: GraphNode['kind']) =>
      nodes.find((n) => n.kind === kind && !n.id.endsWith('-more'))?.id
    const nodeId =
      ev.entity === 'email'
        ? (firstOf('email') ?? 'company')
        : ev.entity === 'app'
          ? (firstOf('app') ?? 'company')
          : ev.entity === 'user'
            ? (firstOf('user') ?? 'company')
            : 'company'

    return { nodeId, at: new Date(ev.ts).getTime() }
  }, [businessEvents, nodes])

  const nodeById = useMemo(() => {
    const map = new Map<string, GraphNode>()
    nodes.forEach((n) => map.set(n.id, n))

    return map
  }, [nodes])

  const nodeLabels = useMemo(() => {
    const labels: Record<string, string> = {}
    nodes.forEach((n) => {
      labels[n.id] = n.label
    })

    return labels
  }, [nodes])

  const selectedNode = selectedId ? (nodeById.get(selectedId) ?? null) : null

  const neighbors = useMemo(() => {
    if (!selectedId) return []
    const ids = new Set<string>()
    edges.forEach((e) => {
      const s = typeof e.source === 'string' ? e.source : e.source.id
      const tg = typeof e.target === 'string' ? e.target : e.target.id
      if (s === selectedId) ids.add(tg)
      if (tg === selectedId) ids.add(s)
    })

    return [...ids]
      .map((id) => nodeById.get(id))
      .filter((n): n is GraphNode => Boolean(n))
  }, [selectedId, edges, nodeById])

  const selectedEdge = selectedEdgeId
    ? (edges.find((e) => e.id === selectedEdgeId) ?? null)
    : null
  const edgeEnd = (end: string | GraphNode) =>
    typeof end === 'string' ? end : end.id
  const edgeSource = selectedEdge
    ? (nodeById.get(edgeEnd(selectedEdge.source)) ?? null)
    : null
  const edgeTarget = selectedEdge
    ? (nodeById.get(edgeEnd(selectedEdge.target)) ?? null)
    : null

  return (
    <div className={cx(styles.view, styles.keyframes)}>
      <Card className={styles.headerCard} styles={{ body: { padding: 0 } }}>
        <KpiStrip kpis={kpis} period={period} onPeriodChange={setPeriod} />
        <AttentionLane
          items={ATTENTION_ITEMS}
          workspaceIdParam={String(workspaceId)}
        />
      </Card>

      <Row gutter={[16, 16]} align="stretch" className={styles.columns}>
        <Col xs={24} xl={16}>
          <Card className={styles.graphCard} styles={{ body: { padding: 0 } }}>
            <Tabs
              activeKey={tab}
              onChange={(k) => setTab(k as GraphTab)}
              tabBarStyle={{ margin: 0, padding: '0 14px' }}
              items={[
                {
                  key: 'graph',
                  label: t('workspaceOverview.companyGraph.tabs.graph')
                },
                {
                  key: 'activity',
                  label: t('workspaceOverview.companyGraph.tabs.activity')
                }
              ]}
            />

            {tab === 'graph' ? (
              <div className={styles.graphTabPanel}>
                <div className={styles.legendBar}>
                  {CATEGORY_ORDER.map((kind) => (
                    <span key={kind} className={styles.legendItem}>
                      <span
                        className={styles.legendSwatch}
                        style={{ background: CATEGORY_COLORS[kind] }}
                        aria-hidden
                      />
                      {t(`workspaceOverview.companyGraph.legend.${kind}`)}
                    </span>
                  ))}
                  <SoonTag style={{ marginInlineStart: 'auto' }}>
                    {t('workspaceOverview.companyGraph.preview')}
                  </SoonTag>
                </div>

                <div className={styles.stageWrap}>
                  <CompanyGraphCanvas
                    nodes={nodes}
                    edges={edges}
                    alerts={alerts}
                    edgePing={edgePing}
                    flash={flash}
                    selectedId={selectedId}
                    selectedEdgeId={selectedEdgeId}
                    hoverId={hoverId}
                    reducedMotion={reducedMotion}
                    onHover={setHoverId}
                    onSelect={selectNode}
                    onSelectEdge={selectEdge}
                  />
                  <ActivityTicker
                    feed={feed}
                    nodeLabels={nodeLabels}
                    onSelect={selectNode}
                  />
                  <NodeDetailPanel
                    node={selectedNode}
                    activity={
                      selectedId ? (nodeActivity[selectedId] ?? []) : []
                    }
                    neighbors={neighbors}
                    workspaceIdParam={String(workspaceId)}
                    onClose={() => setSelectedId(null)}
                    onSelectNode={selectNode}
                  />
                  <EdgeDetailPanel
                    source={edgeSource}
                    target={edgeTarget}
                    events={
                      selectedEdgeId ? (edgeActivity[selectedEdgeId] ?? []) : []
                    }
                    onClose={() => setSelectedEdgeId(null)}
                  />
                </div>
              </div>
            ) : (
              <ActivityFeedTab events={businessEvents} />
            )}
          </Card>
        </Col>

        <Col xs={24} xl={8}>
          <Card className={styles.aiCard} styles={{ body: { padding: 0 } }}>
            <div className={styles.aiHead}>
              <Sparkles size={14} />
              {t('workspaceOverview.companyGraph.tabs.aiInsights')}
            </div>
            <AiInsightsTab events={businessEvents} period={period} />
          </Card>
        </Col>
      </Row>
    </div>
  )
}
